import React from 'react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Shield, FolderOpen, Download, FileAudio, FileText, Lock, Cloud, UserCircle } from 'lucide-react';

const MemberArea = () => {
  const scrollToContact = () => {
    const element = document.getElementById('contact');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const features = [
    {
      icon: FolderOpen,
      title: "Gestion de fichiers",
      description: "Organisez et gérez tous vos fichiers audio, sources, tablatures et extraits dans un espace personnalisé"
    },
    {
      icon: Download,
      title: "Récupération illimitée",
      description: "Téléchargez vos fichiers audio et sources à tout moment, où que vous soyez"
    },
    {
      icon: Shield,
      title: "Espace sécurisé",
      description: "Vos données sont protégées avec un cryptage de niveau bancaire et des sauvegardes quotidiennes"
    },
    {
      icon: Cloud,
      title: "Stockage cloud",
      description: "Tous vos projets sont sauvegardés automatiquement dans le cloud pour une tranquillité d'esprit totale"
    },
    {
      icon: FileAudio,
      title: "Tous formats supportés",
      description: "Audio (WAV, MP3, FLAC), sources (DAW projects), tablatures, partitions et documents"
    },
    {
      icon: UserCircle,
      title: "Données personnelles",
      description: "Gérez vos informations personnelles, préférences et historique de projets en toute simplicité"
    }
  ];

  return (
    <section className="py-24 bg-gradient-to-b from-white via-orange-50/30 to-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center justify-center mb-4">
            <div className="bg-orange-600 p-3 rounded-full">
              <Lock className="w-8 h-8 text-white" />
            </div>
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Votre espace abonné personnel
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Accédez à un espace sécurisé pour gérer tous vos fichiers, projets et données personnelles en toute tranquillité
          </p>
        </div>

        {/* Features Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
          {features.map((feature, index) => {
            const IconComponent = feature.icon;
            return (
              <Card
                key={index}
                className="border-2 border-gray-100 hover:border-orange-500 transition-all duration-300 hover:shadow-lg group"
              >
                <CardContent className="p-6">
                  <div className="bg-orange-50 w-14 h-14 rounded-lg flex items-center justify-center mb-4 group-hover:bg-orange-600 transition-colors duration-300">
                    <IconComponent className="w-7 h-7 text-orange-600 group-hover:text-white transition-colors duration-300" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">
                    {feature.title}
                  </h3>
                  <p className="text-gray-600">
                    {feature.description}
                  </p>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Highlight Box */}
        <div className="bg-gradient-to-br from-orange-600 to-orange-700 rounded-2xl p-8 md:p-12 text-white shadow-2xl">
          <div className="grid md:grid-cols-2 gap-8 items-center">
            <div>
              <h3 className="text-3xl font-bold mb-4">
                Un espace conçu pour les professionnels
              </h3>
              <p className="text-orange-100 mb-6 leading-relaxed">
                Notre espace abonné vous offre un contrôle total sur vos projets. 
                Récupérez vos fichiers audio finaux, vos sources pour des modifications futures, 
                et organisez toutes vos ressources musicales (tablatures, partitions, notes) 
                dans un environnement sécurisé et accessible 24/7.
              </p>
              <ul className="space-y-3 mb-6">
                <li className="flex items-start">
                  <Shield className="w-5 h-5 mr-3 mt-1 flex-shrink-0" />
                  <span>Sécurité maximale avec authentification à deux facteurs</span>
                </li>
                <li className="flex items-start">
                  <Cloud className="w-5 h-5 mr-3 mt-1 flex-shrink-0" />
                  <span>Sauvegarde automatique quotidienne</span>
                </li>
                <li className="flex items-start">
                  <Download className="w-5 h-5 mr-3 mt-1 flex-shrink-0" />
                  <span>Téléchargements illimités pendant toute la durée de votre abonnement</span>
                </li>
              </ul>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20">
              <div className="space-y-4">
                <div className="flex items-center justify-between pb-4 border-b border-white/20">
                  <span className="text-orange-100">Types de fichiers</span>
                  <span className="font-semibold">Tous formats</span>
                </div>
                <div className="flex items-center justify-between pb-4 border-b border-white/20">
                  <span className="text-orange-100">Espace de stockage</span>
                  <span className="font-semibold">50 GB inclus</span>
                </div>
                <div className="flex items-center justify-between pb-4 border-b border-white/20">
                  <span className="text-orange-100">Durée d'archivage</span>
                  <span className="font-semibold">1 an minimum</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-orange-100">Support technique</span>
                  <span className="font-semibold">Prioritaire</span>
                </div>
              </div>
            </div>
          </div>
          <div className="mt-8 text-center md:text-left">
            <Button
              size="lg"
              onClick={scrollToContact}
              className="bg-white hover:bg-orange-50 text-orange-600 font-semibold px-8 py-6 text-lg"
            >
              Découvrir l'espace abonné
            </Button>
          </div>
        </div>

        {/* Security Note */}
        <div className="mt-12 text-center">
          <p className="text-sm text-gray-600">
            <Lock className="w-4 h-4 inline mr-2" />
            Vos données sont cryptées et protégées selon les normes RGPD
          </p>
        </div>
      </div>
    </section>
  );
};

export default MemberArea;
